import { call, delay, select, takeLatest } from 'redux-saga/effects';
import { actions, fetchQCTaskDefinitions } from '@restore/QCTaskDefinition/QCTaskDefinition.slice';
import { actions as qcPatientVisitsDatesActions } from '@restore/QCPatientVisitsDates/QCPatientVisitsDates.slice';
import {
  QCTaskDefinitionFetchAction,
  QCTaskDefinitionUpdate,
} from '@restore/QCTaskDefinition/QCTaskDefinition.type';
import { dispatch, Resp } from '@restore/helpers';
import * as api from '@api/qcTaskDefinitions';
import { redirectToCTListOnError } from '@restore/tools';
import { selectQCTaskState } from '@restore/QCTask/QCTask.slice';
import { TaskDefinitionExecutionNamedStatus } from '@dynamika-shared/types/qcTaskDefinitions';

export function* QCTaskDefinitionsWorker(action: QCTaskDefinitionFetchAction) {
  try {
    const { data }: Resp<typeof api.fetchQCTaskDefinitions> = yield call(
      api.fetchQCTaskDefinitions,
      action.payload.patientId,
      action.payload.visitId,
      action.payload.clinicalTrialId
    );

    yield dispatch(actions.fetchQCTaskDefinitionsSuccess(data));
  } catch (error) {
    yield call(redirectToCTListOnError, error);
    yield dispatch(actions.fetchQCTaskDefinitionsFailure(error as Error));
  }
}

export function* QCTaskDefinitionUpdateWorker(action: QCTaskDefinitionUpdate) {
  try {
    const { data: taskData }: ReturnType<typeof selectQCTaskState> =
      yield select(selectQCTaskState);
    if (taskData) {
      const { visitId, patientId } = taskData;

      const { data }: Resp<typeof api.updateQCTaskDefinition> = yield call(
        api.updateQCTaskDefinition,
        action.payload.taskDefinitionId,
        action.payload.clinicalTrialId,
        {
          patientId: patientId ?? '',
          visitId: visitId,
          comment: '',
        }
      );

      yield dispatch(actions.updateQCTaskDefinitionSuccess(data));

      let statusChanged = false;
      while (!statusChanged) {
        yield delay(5000);

        const currentContext:
          | { visitId: string | null; patientId: string | null }
          | undefined = yield select(
          state => state.qcTaskDefinition.currentContext
        );
        if (
          currentContext?.visitId !== visitId ||
          currentContext?.patientId !== patientId
        ) {
          break;
        }

        const { data }: Resp<typeof api.fetchQCTaskDefinitionStatus> =
          yield call(
            api.fetchQCTaskDefinitionStatus,
            action.payload.taskDefinitionId,
            patientId ?? '',
            visitId,
            action.payload.clinicalTrialId
          );

        yield dispatch(
          qcPatientVisitsDatesActions.fetchQCPatientVisitsDates({
            clinicalTrialId: action.payload.clinicalTrialId,
            patientId: patientId as string,
          })
        );
        if (
          data.executionStatus !==
          TaskDefinitionExecutionNamedStatus.IN_PROGRESS
        ) {
          statusChanged = true;
          yield dispatch(actions.fetchQCTaskDefinitionStatusSuccess(data));
        }

        yield call(
          QCTaskDefinitionsWorker,
          fetchQCTaskDefinitions({
            clinicalTrialId: action.payload.clinicalTrialId,
            visitId,
            patientId
          })
        );
      }
    }
  } catch (error) {
    yield call(redirectToCTListOnError, error);
    yield dispatch(actions.fetchQCTaskDefinitionsFailure(error as Error));
  }
}

export function* QCTaskDefinitionsSaga() {
  yield takeLatest(
    actions.fetchQCTaskDefinitions.type,
    QCTaskDefinitionsWorker
  );
  yield takeLatest(
    actions.updateQCTaskDefinition.type,
    QCTaskDefinitionUpdateWorker
  );
}
